/**
 * DeviceUpdateJobs — per-device ACTIVE update pipeline (admin Lambda, tenant-scoped).
 *
 * Shows the update jobs currently IN FLIGHT against this device — across the three
 * independent systems: OS image (Mender), Content sync, and Greengrass component
 * rollouts. Active = QUEUED + IN_PROGRESS. Terminal jobs are history and are
 * NOT shown here — installed/applied state lives in DeviceStatus (shadow) and
 * DeviceContentCard (content), and the page's "Recent events" table has raw history
 * — with one exception (DE-276): a terminal SUCCEEDED job whose payload is only
 * STAGED (applies at next power-on) is still owed to the device, so it stays on
 * the card until device truth (assigned-vs-applied content, shadow release
 * category) says it landed. Visibility comes from `shouldShowStaged`, never from
 * the job alone — a completed execution's statusDetails can never be revised.
 *
 * The card sits between "what's installed" (DeviceStatus) and "content detail"
 * (DeviceContentCard) as the "what's being pushed right now" bridge. For an active
 * Mender job it shows target → installed (from shadow.gateway.mender_artifact); for
 * an active GG rollout it shows the deployment's components target-vs-installed
 * (from shadow.gateway.gg_components). Reuses the shared jobPill grammar.
 */
import {
  useDeviceContent,
  useDeviceUpdateJobs,
  type DeviceUpdateJob,
  type GroupDeployment,
  type UpdateJobKind,
} from '../api/hooks';
import { jobPill, releaseOutcomePill } from './contentSync';
import { fmtAgo } from './format';
import {
  downloadProgress,
  shouldShowStaged,
  stagedForce,
  stagedStageLine,
  stageOf,
} from './updateJobs';

const KIND_LABEL: Record<UpdateJobKind, string> = {
  mender: 'OS image',
  content: 'Content',
  greengrass: 'Components',
  release: 'Release',
  reboot: 'Reboot',
  uc_logs_bundle: 'uC log bundle',
  other: 'Update',
};

export function DeviceUpdateJobs({
  thing,
  shadow,
  envGroup,
}: {
  thing: string;
  shadow: Record<string, Record<string, unknown> | undefined>;
  envGroup?: string | null;
}) {
  const q = useDeviceUpdateJobs(thing);
  // DE-276 — device truth for staged-row visibility. Shares its React Query key
  // with DeviceContentCard on the same page, so this adds no network request.
  const content = useDeviceContent(thing, envGroup);
  const gateway = shadow?.gateway;
  const installedComponents = (gateway?.gg_components ?? null) as Record<string, string> | null;
  const installedMender = (gateway?.mender_artifact as string | undefined) ?? null;
  const releaseSource = (shadow?.release?.source as string | undefined) ?? null;

  // Not-visible / not-in-registry → omit the card entirely (tenant users without
  // access, or a thing absent from the registry). Genuine errors stay visible.
  if (q.error) {
    const status = (q.error as { status?: number }).status;
    if (status === 403 || status === 404) return null;
  }

  const data = q.data;
  // A staged job an ACTIVE job supersedes (re-deploy of the same kind) would
  // render the same payload twice — the in-flight row wins.
  const activeKinds = new Set((data?.active ?? []).map((j) => j.kind));
  const staged = (data?.staged ?? []).filter(
    (j) =>
      !activeKinds.has(j.kind) &&
      shouldShowStaged(j, content.data?.applied, content.data?.assigned, releaseSource, installedMender),
  );
  const idle =
    Boolean(data) && data!.active.length === 0 && staged.length === 0 && !data!.greengrass;

  return (
    <section className="card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-medium">Active updates</h2>
        <div className="flex items-center gap-2">
          {data && data.active.length > 0 && (
            <span className="pill-accent">
              {data.active.length} in progress
            </span>
          )}
          {staged.length > 0 && (
            <span className="pill-warn">
              {staged.length} staged
            </span>
          )}
        </div>
      </div>

      {q.isLoading && <p className="text-sm text-muted">Loading…</p>}
      {q.error && (q.error as { status?: number }).status !== 403 &&
        (q.error as { status?: number }).status !== 404 && (
          <p className="text-sm text-warn">{(q.error as Error).message}</p>
        )}

      {data && idle && <p className="text-sm text-muted">No updates in progress.</p>}

      {data && !idle && (
        <div className="space-y-1">
          {data.active.map((job) => (
            <JobRow key={job.job_id} job={job} installedMender={installedMender} />
          ))}
          {staged.map((job) => (
            <StagedRow key={job.job_id} job={job} />
          ))}
          {data.greengrass && (
            <GreengrassPanel deployment={data.greengrass} installed={installedComponents} />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * DE-276 — a terminal job whose payload is staged but not yet applied. Shown
 * only while device truth says the update is still owed (see shouldShowStaged),
 * so unlike the Releases ledger this row cannot outlive the actual apply.
 */
function StagedRow({ job }: { job: DeviceUpdateJob }) {
  const pill =
    releaseOutcomePill(job.status, job.status_details, stagedForce(job.status_details)) ??
    jobPill(job);
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="pill-muted">{KIND_LABEL[job.kind] ?? 'Update'}</span>
          {job.title && (
            <span className="font-mono text-xs text-fg truncate">{job.title}</span>
          )}
        </div>
        <p className="text-[11px] text-warn font-medium mt-0.5">
          {stagedStageLine(job.status_details)}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {job.last_updated_at && (
          <span className="text-[11px] text-muted" title={job.last_updated_at}>
            {fmtAgo(job.last_updated_at)}
          </span>
        )}
        <span className={pill.cls} title={pill.title}>
          {pill.label}
        </span>
      </div>
    </div>
  );
}

function JobRow({
  job,
  installedMender,
}: {
  job: DeviceUpdateJob;
  installedMender: string | null;
}) {
  const phase = job.status_details?.phase ?? job.status_details?.step ?? null;
  // DE-180: awaiting_user is the one operator-actionable state — promote the
  // pill to a warning "Action needed" instead of the generic "In progress".
  const awaiting = phase === 'awaiting_user';
  const stage = stageOf(job);
  const progress = downloadProgress(job);
  // A download that keeps failing is operator-relevant, so it gets the same
  // promotion awaiting_user does. Before agent 1.9.0 there was nothing to
  // promote — a stalled download reported the same frozen byte count as a
  // healthy one, which is how …057 read "87 %" for three days.
  const retrying = progress?.retrying ?? false;
  const basePill = jobPill(job);
  const pill = awaiting
    ? { ...basePill, label: 'Action needed', cls: 'pill-warn' }
    : retrying
      ? { ...basePill, label: 'Retrying', cls: 'pill-warn' }
      : basePill;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="pill-muted">{KIND_LABEL[job.kind] ?? 'Update'}</span>
          {job.title && (
            <span className="font-mono text-xs text-fg truncate">{job.title}</span>
          )}
        </div>
        {((job.kind === 'mender') ||
          (job.kind === 'release' && job.parts?.os != null)) &&
          installedMender && (
            <p className="text-[11px] text-muted mt-0.5">currently {installedMender}</p>
          )}
        {awaiting ? (
          // DE-180: explicit call-to-action — the release waits until someone
          // accepts it on the unit itself.
          <p className="text-[11px] text-warn font-medium mt-0.5">
            ⚠ Awaiting acceptance — tap “Update Now” on the device
          </p>
        ) : progress ? (
          <div className="mt-0.5">
            <p className={`text-[11px] ${retrying ? 'text-warn font-medium' : 'text-muted'}`}>
              {progress.label}
            </p>
            {progress.pct != null && (
              <div className="h-1 w-44 bg-soft rounded-full mt-1 overflow-hidden">
                {/* A stalled bar must not read as progress — mute the fill so a
                    frozen 87 % looks stopped rather than nearly finished. */}
                <div
                  className={`h-full rounded-full ${retrying ? 'bg-muted' : 'bg-accent'}`}
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
            )}
          </div>
        ) : (
          stage && <p className="text-[11px] text-muted mt-0.5">{stage}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {job.last_updated_at && (
          <span className="text-[11px] text-muted" title={job.last_updated_at}>
            {fmtAgo(job.last_updated_at)}
          </span>
        )}
        <span className={pill.cls} title={pill.title}>
          {pill.label}
        </span>
      </div>
    </div>
  );
}

// Target-vs-installed for the components an active GG rollout delivers. Target =
// the env-group's latest deployment (server-sorted, artome.* first); installed =
// shadow.gateway.gg_components. Don't assert drift when installed is unknown.
function GreengrassPanel({
  deployment,
  installed,
}: {
  deployment: GroupDeployment;
  installed: Record<string, string> | null;
}) {
  return (
    <div className="pt-2">
      <h3 className="text-xs uppercase tracking-wide text-muted mb-1">
        Components being deployed
      </h3>
      <p className="text-[11px] text-muted mb-1">
        <span className="font-medium">
          {deployment.deployment_name ?? '(unnamed deployment)'}
        </span>{' '}
        rev {deployment.revision_id}
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted border-b border-border">
            <th className="font-medium py-1">Component</th>
            <th className="font-medium py-1 text-right">Target</th>
            <th className="font-medium py-1 text-right">Installed</th>
            <th className="font-medium py-1 w-5" />
          </tr>
        </thead>
        <tbody>
          {deployment.components.map((c) => {
            const have = installed?.[c.name] ?? null;
            const known = have !== null;
            const match = known && have === c.version;
            return (
              <tr key={c.name} className="border-b border-border last:border-0">
                <td className="py-1 font-mono">{c.name}</td>
                <td className="py-1 tabular-nums text-right">{c.version}</td>
                <td className="py-1 tabular-nums text-right text-muted">{have ?? '—'}</td>
                <td className="py-1 text-right">
                  {known ? (
                    match ? (
                      <span className="text-accent" title="installed matches target">✓</span>
                    ) : (
                      <span className="text-warn" title="installed differs from target">⚠</span>
                    )
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
